import { z } from 'zod';
import { WIDGETS_BY_ID } from './dashboard-widgets.js';
/**
 * A saved dashboard: which widgets, where, and with what settings.
 *
 * Items use react-grid-layout's own item shape ({i, x, y, w, h, minW, minH,
 * static}) with the widget fields alongside, so drag-and-drop can arrive later
 * by handing these items straight to <ResponsiveGridLayout> — no migration.
 *
 * Saving never fails over a widget: an unknown widget is dropped and a bad
 * setting reset to its default, with a note of what changed. A layout saved by
 * a newer build should degrade on an older one, not vanish.
 */
export const LAYOUT_SCHEMA_VERSION = 1;
export const LAYOUT_COLS = 12;
export const LAYOUT_MAX_ITEMS = 60;
export const WIDGET_SCOPES = ['combined', 'mlf', 'mff'];
const looseItemSchema = z.object({
    i: z.string().min(1).max(40),
    x: z.number().int(),
    y: z.number().int(),
    w: z.number().int(),
    h: z.number().int(),
    minW: z.number().int().optional(),
    minH: z.number().int().optional(),
    static: z.boolean().optional(),
    widget: z.string().min(1).max(60),
    config: z.record(z.unknown()).optional(),
    scope: z.enum(WIDGET_SCOPES).optional().catch(undefined),
    title: z.string().max(80).optional().catch(undefined),
});
const looseLayoutSchema = z.object({
    schemaVersion: z.number().int().optional(),
    rowHeight: z.number().int().min(40).max(200).optional(),
    items: z.array(z.unknown()).max(LAYOUT_MAX_ITEMS * 2),
});
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
/**
 * Makes any stored or submitted layout safe to render. Throws only when the
 * input is not a layout at all (no items array).
 */
export function validateLayout(raw, widgets = WIDGETS_BY_ID) {
    const parsed = looseLayoutSchema.safeParse(raw);
    if (!parsed.success)
        throw new Error('Not a dashboard layout');
    const changes = [];
    const items = [];
    const seen = new Set();
    parsed.data.items.forEach((candidate, index) => {
        const item = looseItemSchema.safeParse(candidate);
        if (!item.success) {
            changes.push(`Item ${index + 1} was malformed and was removed`);
            return;
        }
        const def = widgets.get(item.data.widget);
        if (!def) {
            changes.push(`Removed "${item.data.widget}", a widget this version does not have`);
            return;
        }
        if (items.length >= LAYOUT_MAX_ITEMS) {
            changes.push(`Only ${LAYOUT_MAX_ITEMS} widgets fit on one dashboard; the rest were removed`);
            return;
        }
        let config = def.defaultConfig;
        const configResult = def.configSchema.safeParse(item.data.config ?? {});
        if (configResult.success)
            config = configResult.data;
        else
            changes.push(`Reset the settings of "${item.data.title ?? def.title}"`);
        let id = item.data.i;
        while (seen.has(id))
            id = `${item.data.i}-${seen.size}`;
        seen.add(id);
        const minW = clamp(Math.max(item.data.minW ?? 0, def.minSize.w), 1, LAYOUT_COLS);
        const minH = clamp(Math.max(item.data.minH ?? 0, def.minSize.h), 1, 20);
        const w = clamp(item.data.w, minW, LAYOUT_COLS);
        const h = clamp(item.data.h, minH, 20);
        items.push({
            i: id,
            x: clamp(item.data.x, 0, LAYOUT_COLS - w),
            y: Math.max(0, item.data.y),
            w,
            h,
            minW,
            minH,
            ...(item.data.static ? { static: true } : {}),
            widget: def.id,
            config,
            ...(item.data.scope && def.siteAware && item.data.scope !== 'combined' ? { scope: item.data.scope } : {}),
            ...(item.data.title ? { title: item.data.title } : {}),
        });
    });
    return {
        layout: {
            schemaVersion: LAYOUT_SCHEMA_VERSION,
            cols: LAYOUT_COLS,
            rowHeight: parsed.data.rowHeight ?? 80,
            items: compactLayout(items),
        },
        changes,
    };
}
const overlaps = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
/**
 * Floats every item up as far as it will go without overlapping another,
 * top-to-bottom and left-to-right — react-grid-layout's vertical compaction,
 * so a layout looks the same before and after drag-and-drop arrives.
 */
export function compactLayout(items) {
    const sorted = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
    const placed = [];
    for (const item of sorted) {
        const next = { ...item, y: 0 };
        while (placed.some((p) => overlaps(p, next)))
            next.y += 1;
        // Never move an item down past where it was put.
        if (next.y > item.y)
            next.y = item.y;
        while (placed.some((p) => overlaps(p, next)))
            next.y += 1;
        placed.push(next);
    }
    return placed;
}
/** Where a new widget goes: the bottom-left, under everything else. */
export function appendItem(layout, item) {
    const bottom = layout.items.reduce((max, i) => Math.max(max, i.y + i.h), 0);
    return { ...layout, items: [...layout.items, { ...item, x: 0, y: bottom }] };
}
/** A fresh item id that no item in the layout uses. */
export function newItemId(layout, widget) {
    const used = new Set(layout.items.map((i) => i.i));
    for (let n = 1;; n++) {
        const id = `${widget}-${n}`;
        if (!used.has(id))
            return id;
    }
}
//# sourceMappingURL=dashboard-layout.js.map